/* eslint-disable react/prop-types */
/* eslint-disable react-refresh/only-export-components */
import {
  createSubscription,
  getSubscription,
  getUsageApi,
  getUserProfile,
  loginApi,
  paymentPhonePeApi,
  registerApi,
} from "@/app/api";
import apiClient from "@/app/axios";
import Loader from "@/components/custom/Loader";
import { signInWithProvider } from "@/config/firebase";
import {
  initialSubscriptionDetails,
  STORAGE_KEYS,
} from "@/modules/home/constant";
import { handleResponseError } from "@/utils/errorHandler";
import { toast } from "@/utils/useToast";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";


const UserContext = createContext();

export const useUser = () => useContext(UserContext);

// UserProvider Component
export const UserProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageCard, setUsageCard] = useState(null);
  const [requestInProgress, setRequestInProgress] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(
    initialSubscriptionDetails
  );
  useEffect(() => {
    const storedToken = localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE);
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

    // Fast-path: restore from localStorage
    if (storedToken) {
      setToken(storedToken);
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        setLoading(false);
        return;
      }

      // If token exists but user is missing, try to fetch from backend
      (async () => {
        try {
          const res = await getUserProfile();
          const profile = res?.data?.data || res?.data;
          setUser(profile);
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(profile));
        } catch {
          // Token likely invalid/expired
          localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE);
          localStorage.removeItem(STORAGE_KEYS.USER);
          localStorage.removeItem(STORAGE_KEYS.SUB);
          setToken(null);
          setUser(null);
        } finally {
          setLoading(false);
        }
      })();

      return;
    }

    setLoading(false);
  }, []);

  const loginByProvider = async (provider) => {
    setLoading(true);
    setError(null);

    try {
      const idToken = await signInWithProvider(provider);
      const response = await apiClient.post("/auth/social-login", {
        idToken,
        provider,
      });

      if (response.data.token) {
        localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE, response.data.token);
        localStorage.setItem(
          STORAGE_KEYS.USER,
          JSON.stringify(response.data.user)
        );
        localStorage.setItem(STORAGE_KEYS.SUB, response.data.subscription);
        setToken(response.data.token);
        setUser(response.data.user);
        navigate("/");
        return { success: true };
      }

      throw new Error("Login failed");
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Login failed";
      setError(errorMessage);
      await handleResponseError(err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const registerByProvider = async (provider) => {
    setLoading(true);
    setError(null);

    try {
      const idToken = await signInWithProvider(provider);
      const response = await apiClient.post("/auth/social-login", {
        idToken,
        provider,
      });

      if (response.data.token) {
        localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE, response.data.token);
        localStorage.setItem(
          STORAGE_KEYS.USER,
          JSON.stringify(response.data.user)
        );
        localStorage.setItem(STORAGE_KEYS.SUB, response.data.subscription);
        setToken(response.data.token);
        setUser(response.data.user);
        navigate("/");
        return { success: true };
      }

      throw new Error("Registration failed");
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Registration failed";
      setError(errorMessage);
      await handleResponseError(err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.SUB);
    setToken(null);
    setUser(null);
    navigate("/");

    toast.info("Logged out successfully");
  };

  const getProfileDetails = async () => {
    setRequestInProgress(true);
    try {
      const res = await getUserProfile();
      const profile = res?.data?.data || res?.data;
      setUser(profile);
      setRequestInProgress(false);
    } catch (error) {
      setRequestInProgress(false);
      await handleResponseError(error);
    }
  };

  const getUsage = async () => {
    setRequestInProgress(true);
    try {
      const res = await getUsageApi();

      // API shape:
      // { success, message, data: { usageSummary: {...}, usageList: [...] } }
      const payload = res?.data?.data || res?.data || {};
      const { usageSummary = {}, usageList = [] } = payload;

      setUsage(usageList);
      setUsageCard(usageSummary);
      setRequestInProgress(false);
    } catch (error) {
      setRequestInProgress(false);
      await handleResponseError(error);
    }
  };

  const loginLocal = async (data) => {
    setLoading(true);
    try {
      const res = await loginApi(data);
      const { token = {}, user = {} } = res.data;

      if (token) {
        localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE, token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
        setToken(token);
        setUser(user);
        setLoading(false);
        navigate("/");
      }
    } catch (error) {
      setLoading(false);
      await handleResponseError(error);
    }
  };

  const registerLocal = async (data) => {
    setLoading(true);
    try {
      const res = await registerApi(data);
      const { token = {}, user = {} } = res.data;

      if (token) {
        localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE, token);
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
        setToken(token);
        setUser(user);
        setLoading(false);
        navigate("/");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      await handleResponseError(error);
    }
  };

  const fetchSubscription = async () => {
    setRequestInProgress(true);
    try {
      const res = await getSubscription();
      const payload = res?.data?.data || res?.data || {};
      const { subscription } = payload;

      // API can return subscription: null
      if (subscription) {
        setSubscriptionDetails(subscription);
      } else {
        setSubscriptionDetails(initialSubscriptionDetails);
      }

      setRequestInProgress(false);
    } catch (error) {
      setRequestInProgress(false);
      await handleResponseError(error);
    }
  };

  const subscribe = async (data) => {
    setRequestInProgress(true);
    try {
      const res = await createSubscription(data);
      const { subscription, razorpaySubscription } = res.data;
      if (subscription) {
        setSubscriptionDetails(subscription);
      }
      if (razorpaySubscription && razorpaySubscription.short_url) {
        // Instead of opening Razorpay modal, redirect to the short URL
        window.location.href = razorpaySubscription.short_url;
      }
      setRequestInProgress(false);
    } catch (error) {
      setRequestInProgress(false);
      await handleResponseError(error);
    }
  };

  const paymentPhonePe = async (data) => {
    setRequestInProgress(true);
    try {
      const res = await paymentPhonePeApi(data);
      const { success, redirectUrl } = res.data;
      if (success) {
        // const res = await getSubscription();
        // const { subscription } = res.data;
        // if (subscription) {
        //   setSubscriptionDetails(subscription);
        // }

        window.open(redirectUrl, '_blank');
      }
      // open in seperate page the redirectUrl
      setRequestInProgress(false);
    } catch (error) {
      setRequestInProgress(false);
      await handleResponseError(error);
    }
  };

  return (
    <UserContext.Provider
      value={{
        subscribe,
        subscriptionDetails,
        fetchSubscription,
        registerLocal,
        loginLocal,
        usage,
        usageCard,
        getUsage,
        requestInProgress,
        user,
        getProfileDetails,
        token,
        loading,
        loginByProvider,
        registerByProvider,
        logout,
        error,
        paymentPhonePe
      }}
    >
      {loading ? (
        <div className="flex items-center justify-center h-screen bg-gray-900 text-white">
          <Loader message="Checking authentication..." />
        </div>
      ) : (
        children
      )}
    </UserContext.Provider>
  );
};
